import React, { useEffect, useRef, useState } from "react";
import { HeaderContainer } from "./viewreport";
import { PixelStyle } from "../../components/layout";
import { Trash2 } from "lucide-react";
import { SearchableDropdown } from "../../components/reportComponents";
import { fetchAllCompanyList } from "../../apis/company/companyList";
import { CreateTourAPI } from "../../apis/tour";
// import Barcode from "react-barcode";
import { useGeolocated } from "react-geolocated";
import QRCode from "react-qr-code";
import {
  copyQRCodeToClipboard,
  HandleCopyToClipboard,
} from "../../utils/CopyToClipboard";
import { generateQrcodePDF } from "../../components/functions/generateQrcodePDF";
import { BsCopy, BsPrinter } from "react-icons/bs";
import { AlertModal } from "../../components/Modal";

const CreateTour = () => {
  const [tourName, setTourName] = useState("");
  const [companyList, setCompanyList] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [companyLocation, setCompanyLocation] = useState("");
  const [companyLocationId, setCompanyLocationId] = useState("");
  const [checkpoints, setCheckpoints] = useState([
    { id: 1, name: "", lat: "", lon: "", location: "", qrcode: "" },
  ]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("info");

  const { coords, isGeolocationAvailable, isGeolocationEnabled } =
    useGeolocated({
      positionOptions: {
        enableHighAccuracy: true,
      },
      userDecisionTimeout: 5000,
    });

  const [distance, setDistance] = useState("");

  useEffect(() => {
    fetchAllCompanyList()
      .then((data) => setCompanyList(data))
      .catch((err) => setError("Failed to fetch company list"));
  }, []);

  const addCheckpoint = () => {
    const newId = checkpoints.length + 1;
    setCheckpoints([
      ...checkpoints,
      { id: newId, name: "", lat: "", lon: "", location: "", qrcode: "" },
    ]);
  };

  const updateCheckpoint = (id, updates) => {
    setCheckpoints(
      checkpoints.map((cp) => (cp.id === id ? { ...cp, ...updates } : cp))
    );
  };

  const removeCheckpoint = (id) => {
    if (checkpoints.length === 1) {
      setAlertMessage("At least one checkpoint is required");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    setCheckpoints(checkpoints.filter((cp) => cp.id !== id));
  };

  const fetchLocation = (checkpointId) => {
    if (!isGeolocationAvailable) {
      setAlertMessage("Geolocation is not supported by your browser");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    if (!isGeolocationEnabled) {
      setAlertMessage("Please enable geolocation permissions");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    if (coords) {
      const lat = coords.latitude.toString();
      const lon = coords.longitude.toString();
      const location = `${lat},${lon}`;
      const qrcodeValue = location;
      updateCheckpoint(checkpointId, {
        lat,
        lon,
        location,
        qrcode: qrcodeValue,
      });
      setAlertMessage("Location fetched successfully!");
      setAlertType("success");
      setShowAlert(true);
    } else {
      setAlertMessage("Unable to fetch location. Please try again.");
      setAlertType("error");
      setShowAlert(true);
    }
  };

  const generateQrCode = (checkpointId) => {
    const checkpoint = checkpoints.find((cp) => cp.id === checkpointId);
    if (!checkpoint.lat || !checkpoint.lon) {
      setAlertMessage("Both latitude and longitude are required for QR code");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    const location = `${checkpoint.lat},${checkpoint.lon}`;
    updateCheckpoint(checkpointId, {
      location,
      qrcode: location,
    });
    setAlertMessage("QR code generated successfully!");
    setAlertType("success");
    setShowAlert(true);
  };

  const CreateTourHandler = async () => {
    // Validation
    if (!tourName?.trim()) {
      setAlertMessage("Please enter a tour name");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    if (!companyId || !companyName) {
      setAlertMessage("Please select a company");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    // Note: companyLocationId is set but may not be required by API
    if (checkpoints.length === 0) {
      setAlertMessage("At least one checkpoint is required");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    if (checkpoints.some((cp) => !cp.name?.trim() || !cp.location?.trim())) {
      setAlertMessage("All checkpoints must have a name and location");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    
    // Validate coordinates format
    const invalidCheckpoints = checkpoints.filter(
      (cp) => cp.location && !/^-?\d+\.?\d*,-?\d+\.?\d*$/.test(cp.location)
    );
    if (invalidCheckpoints.length > 0) {
      setAlertMessage("Invalid location format. Please use latitude,longitude format");
      setAlertType("error");
      setShowAlert(true);
      return;
    }

    const tourData = {
      tourName: tourName.trim(),
      companyId,
      companyName,
      distance: distance?.toString() || "0",
      status: "active",
      checkpoints: checkpoints.map((cp, index) => ({
        checkpointName: cp.name.trim(),
        location: cp.location.trim(),
        qrcode: cp.qrcode || cp.location.trim(),
        sequence: (index + 1).toString(),
      })),
    };

    setLoading(true);
    setError(null);

    try {
      const response = await CreateTourAPI(tourData);
      console.log("Tour created successfully:", response);
      setAlertMessage("Tour created successfully!");
      setAlertType("success");
      setShowAlert(true);
      setTourName("");
      setDistance("");
      setCompanyName("");
      setCompanyId("");
      setCompanyLocation("");
      setCompanyLocationId("");
      setCheckpoints([
        { id: 1, name: "", lat: "", lon: "", location: "", qrcode: "" },
      ]);
    } catch (error) {
      setAlertMessage(`Failed to create tour: ${error.message}`);
      setAlertType("error");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const qrRef = useRef();

  return (
    <div className={`w-full text-sm h-full ${PixelStyle}`}>
      <HeaderContainer pageTitle="Create Tour" isRedirecting={false}>
        <span className="text-black">Create Tour</span>
      </HeaderContainer>

      <div className=" space-y-4">
        {error && (
          <div className="bg-red-100 text-red-700 p-3 rounded">{error}</div>
        )}
        <div className="w-full flex items-center justify-between gap-10 bg-white shadow p-3 rounded-lg">
          <div className="flex items-center gap-3 text-nowrap w-full">
            <input
              placeholder="Tour Name"
              className="border p-2 rounded w-full"
              value={tourName}
              onChange={(e) => setTourName(e.target.value)}
              aria-label="Tour name"
              required
            />
            <SearchableDropdown
              users={companyList}
              selectedCompany={companyName}
              setCompanyId={setCompanyId}
              onSelect={setCompanyName}
              setCompanyLocationId={setCompanyLocationId}
              setCompanyLocation={setCompanyLocation}
            />
            <div className="border border-zinc-200 p-2 rounded-md w-full text-nowrap overflow-x-auto min-w-40  scrollHeight">
              {companyLocation ? (
                companyLocation
              ) : (
                <span className="text-zinc-400">Location</span>
              )}
            </div>


            <input
              type="number"
              min="0"
              step="0.1"
              placeholder="Distance (km)"
              className="border p-2 rounded w-20"
              value={distance}
              onChange={(e) => setDistance(e.target.value)}
              aria-label="Tour distance in kilometers"
            />


            <button
              onClick={addCheckpoint}
              className="border px-4 py-2 rounded text-sm bg-white"
            >
              + Add Checkpoint
            </button>
            <button
              onClick={CreateTourHandler}
              disabled={loading}
              className={`bg-blue-600 text-white px-4 py-2 rounded text-sm ${
                loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700"
              }`}
              aria-label="Save tour"
            >
              {loading ? "Creating..." : "Save Tour"}
            </button>
          </div>
        </div>

        <div className="w-full bg-white shadow p-3 rounded-lg">
          <table className="w-full border">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2">Sequence</th>
                <th className="border px-4 py-2">Checkpoint Name</th>
                <th className="border px-4 py-2">Location</th>
                <th className="border px-4 py-2">Qr Code</th>
                <th className="border px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {checkpoints.map((cp, idx) => {
                const hasCoordinates = cp.lat && cp.lon;
                return (
                  <tr key={cp.id}>
                    <td className="border px-4 py-2 text-center">{idx + 1}</td>
                    <td className="border px-4 py-2">
                      <input
                        value={cp.name}
                        onChange={(e) =>
                          updateCheckpoint(cp.id, { name: e.target.value })
                        }
                        className="w-full border rounded p-1"
                        placeholder="Checkpoint Name"
                      />
                    </td>
                    <td className="border px-4 py-2">
                      <span className="flex items-center gap-2">
                        <input
                          type="number"
                          step="any"
                          placeholder="Latitude"
                          value={cp.lat}
                          onChange={(e) => {
                            const lat = e.target.value;
                            const location =
                              lat && cp.lon ? `${lat},${cp.lon}` : "";
                            updateCheckpoint(cp.id, { lat, location });
                          }}
                          className="w-28 border rounded p-1"
                        />
                        <input
                          type="number"
                          step="any"
                          placeholder="Longitude"
                          value={cp.lon}
                          onChange={(e) => {
                            const lon = e.target.value;
                            const location =
                              cp.lat && lon ? `${cp.lat},${lon}` : "";
                            updateCheckpoint(cp.id, { lon, location });
                          }}
                          className="w-28 border rounded p-1"
                        />
                        {!hasCoordinates && (
                          <button
                            onClick={() => fetchLocation(cp.id)}
                            className="bg-[#00325d] text-white px-2 py-1 rounded hover:bg-[#004a7a]"
                            title="Fetch Location"
                            aria-label={`Fetch location for checkpoint ${idx + 1}`}
                          >
                            Fetch
                          </button>
                        )}
                        {hasCoordinates && (
                          <button
                            onClick={() => generateQrCode(cp.id)}
                            className="bg-[#00325d] text-white px-2 py-1 rounded hover:bg-[#004a7a]"
                            aria-label={`Generate QR code for checkpoint ${idx + 1}`}
                          >
                            Generate
                          </button>
                        )}
                        <button
                          onClick={() => {
                            updateCheckpoint(cp.id, {
                              lat: "",
                              lon: "",
                              location: "",
                              qrcode: "",
                            });
                          }}
                          className="bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600"
                          aria-label={`Clear coordinates for checkpoint ${idx + 1}`}
                        >
                          Clear
                        </button>
                      </span>
                    </td>
                    <td className="border px-4 py-2">
                      {/* {cp.qrcode && <Barcode value={cp.qrcode} height={32} />} */}
                      <div ref={qrRef}>
                        {cp.qrcode && (
                          <QRCode
                            size={50}
                            style={{
                              height: "auto",
                              maxWidth: "100%",
                              width: "100%",
                            }}
                            value={cp.qrcode}
                            viewBox={`0 0 256 256`}
                          />
                        )}
                      </div>
                    </td>
                    <td className="border px-4 py-2">
                      <div className="flex items-center gap-2">
                        {cp.qrcode && (
                          <>
                            <button
                              onClick={(e) =>
                                copyQRCodeToClipboard(
                                  e.currentTarget
                                    .closest("tr")
                                    .querySelector("svg"),
                                  (message) => {
                                    setAlertMessage(message);
                                    setAlertType("success");
                                    setShowAlert(true);
                                  },
                                  (error) => {
                                    setAlertMessage(error);
                                    setAlertType("error");
                                    setShowAlert(true);
                                  }
                                )
                              }
                              className="text-[#00325d]  hover:bg-blue-100 p-1.5 rounded-md"
                              aria-label={`Copy QR code for checkpoint ${cp.sequence}`}
                            >
                              <BsCopy size={18} />
                            </button>
                            <button
                              onClick={async () => {
                                const success = await generateQrcodePDF(cp.qrcode, cp.name, (error) => {
                                  setAlertMessage(error);
                                  setAlertType("error");
                                  setShowAlert(true);
                                });
                                if (success) {
                                  setAlertMessage("PDF generated successfully!");
                                  setAlertType("success");
                                  setShowAlert(true);
                                }
                              }}
                              className="text-[#00325d]  hover:bg-blue-100 p-1.5 rounded-md"
                              aria-label={`Generate QR code PDF for checkpoint ${cp.sequence}`}
                            >
                              <BsPrinter size={18} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => removeCheckpoint(cp.id)}
                          aria-label={`Delete checkpoint ${idx + 1}`}
                          className="text-[#00325d] hover:bg-red-100 p-1.5 rounded-md"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        title="Tour Management"
        message={alertMessage}
        type={alertType}
        buttonText="OK"
      />
    </div>
  );
};

export default CreateTour;
